import { useState } from 'react';
import { X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddSchoolScheduleModal({ onClose, onSuccess }) {
  const [mode, setMode] = useState('manual'); // 'manual' or 'upload'
  const [formData, setFormData] = useState({
    class_name: '',
    instructor: '',
    location: '',
    days_of_week: [],
    start_time: '',
    end_time: '',
    color: '#3B82F6'
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  const daysOfWeek = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' }
  ];

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.class_name || formData.days_of_week.length === 0 || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/study-buddy/class-schedule/manual`,
        null,
        {
          params: {
            ...formData,
            days_of_week: formData.days_of_week.join(',')
          },
          withCredentials: true
        }
      );
      
      toast.success('Class added to schedule!');
      onSuccess();
    } catch (error) {
      console.error('Error adding class:', error);
      toast.error(error.response?.data?.detail || 'Failed to add class');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    setLoading(true);
    setUploadProgress('Uploading file...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      setUploadProgress('AI is analyzing your schedule...');
      
      const response = await axios.post(
        `${API}/study-buddy/class-schedule/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );
      
      toast.success(response.data.message || 'Schedule imported successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error uploading schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload schedule');
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-[#F4E4C1] to-white rounded-lg max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-[#E85D4D]"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold text-black mb-2">Add School Schedule</h2>
        <p className="text-sm text-black mb-6">Add your class schedule to the calendar</p>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setMode('manual')}
            className={`flex-1 px-4 py-2 rounded-lg font-bold transition-colors ${
              mode === 'manual'
                ? 'bg-[#E8934D] text-white'
                : 'bg-gray-200 text-black hover:bg-gray-300'
            }`}
          >
            Manual Entry
          </button>
          <button
            onClick={() => setMode('upload')}
            className={`flex-1 px-4 py-2 rounded-lg font-bold transition-colors ${
              mode === 'upload'
                ? 'bg-[#E8934D] text-white'
                : 'bg-gray-200 text-black hover:bg-gray-300'
            }`}
          >
            Upload Image/PDF
          </button>
        </div>

        {mode === 'manual' ? (
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-black mb-2">
                Class Name *
              </label>
              <input
                type="text"
                value={formData.class_name}
                onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
                placeholder="e.g., Biology 101"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold text-black mb-2">
                  Instructor
                </label>
                <input
                  type="text"
                  value={formData.instructor}
                  onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
                  placeholder="Professor name"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-black mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
                  placeholder="Room 203"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-black mb-2">
                Days of Week *
              </label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map(day => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDay(day.value)}
                    className={`px-4 py-2 rounded-lg font-bold transition-colors ${
                      formData.days_of_week.includes(day.value)
                        ? 'bg-[#E8934D] text-white'
                        : 'bg-gray-200 text-black hover:bg-gray-300'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold text-black mb-2">
                  Start Time *
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-black mb-2">
                  End Time *
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
                  required
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-[#E8B67A] text-black font-bold rounded-lg hover:bg-[#E8B67A]/20 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add Class'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleFileUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-black mb-2">
                Upload Schedule (Image or PDF) *
              </label>
              <div className="border-2 border-dashed border-[#E8B67A] rounded-lg p-6 text-center">
                <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                  id="schedule-upload"
                />
                <label
                  htmlFor="schedule-upload"
                  className="cursor-pointer text-black hover:text-[#E85D4D] font-bold"
                >
                  Click to upload or drag and drop
                </label>
                <p className="text-xs text-black mt-2">
                  Supports: JPG, PNG, PDF
                </p>
                {file && (
                  <p className="text-sm text-black mt-3 font-semibold">
                    Selected: {file.name}
                  </p>
                )}
              </div>
            </div>

            {uploadProgress && (
              <div className="bg-blue-100 border border-blue-300 rounded-lg p-4 text-center">
                <p className="text-black font-semibold">{uploadProgress}</p>
                <div className="mt-2 animate-pulse">
                  <div className="h-2 bg-blue-500 rounded"></div>
                </div>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
              <p className="text-sm text-black">
                <strong>Tip:</strong> For best results, ensure your schedule image or PDF clearly shows:
                class names, days of week, and times.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-[#E8B67A] text-black font-bold rounded-lg hover:bg-[#E8B67A]/20 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !file}
                className="flex-1 px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors disabled:opacity-50"
              >
                {loading ? uploadProgress : 'Upload & Extract'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
